import './utils/suppressWarnings';
import ReactDOM from 'react-dom/client';
import { enableMapSet } from 'immer';
import { RouterProvider } from '@tanstack/react-router';
import { router, basepath } from '@src/Router';
import { Provider } from 'redux-bundler-react';
import NavbarNavigateWarningModal from '@pages/Forms/components/modals/NavbarNavigateWarningModal';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from 'react-oidc-context';
import { queryClient } from '@api/queryClient';
import { oidcConfig } from '@src/api/services/auth/authConfig';
import cache from './cache';
import getStore from './app-bundles';
import { setStore } from './utils/store-reference';
import { useModal } from '@stores';

enableMapSet();

const getPathWithoutBasepath = (path) => {
  if (basepath === '/') return path;
  return path.startsWith(basepath) ? path.slice(basepath.length) || '/' : path;
};

const formsNavigationWarningHandler = (event) => {
  let target = event.target;
  while (target && target.nodeName !== 'A') {
    target = target.parentNode;
  }

  const href = target?.getAttribute?.('href');
  if (!href || href === '#' || href.startsWith('#')) {
    return;
  }
  const isLeftClick = event.button === 0;
  const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

  if (!isLeftClick || isModifiedClick) {
    return;
  }
  const targetUrl = new URL(href, window.location.origin);
  if (targetUrl.origin !== window.location.origin) {
    return;
  }
  const targetPath = targetUrl.pathname;
  const currentPath = window.location.pathname;
  const relativeTargetPath = getPathWithoutBasepath(targetPath);
  const relativeCurrentPath = getPathWithoutBasepath(currentPath);

  const isOnFormsRoute = relativeCurrentPath === '/forms' || relativeCurrentPath.startsWith('/forms/');
  const isNavigatingOutsideForms = relativeTargetPath !== '/forms' && !relativeTargetPath.startsWith('/forms/');

  if (isOnFormsRoute && isNavigatingOutsideForms) {
    event.preventDefault(); // Prevent default navigation
    event.stopPropagation();

    useModal.getState().openModal(NavbarNavigateWarningModal, {
      url: relativeTargetPath,
      openInNewTab: target.getAttribute('target') === '_blank',
    });
  }
};
cache.getAll().then((initialData) => {
  const store = getStore(initialData);
  setStore(store);
  if (import.meta.env.VITE_ENVIRONMENT === 'local') {
    window.store = store;
  }
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <div onClickCapture={formsNavigationWarningHandler}>
            <RouterProvider router={router} />
          </div>
        </Provider>
        <ReactQueryDevtools buttonPosition='bottom-left' initialIsOpen={false} />
      </QueryClientProvider>
    </AuthProvider>
  );
});
